import { Directive, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { DynamicFormService } from '@ntap/common';
import {
  BusinessItem,
  Item,
  ItemInfo,
  ItemViewProviderDirective,
  ListView,
  ListViewProviderDirective
} from '@ntap/core';
import { Observable, skip, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

export type CompoundViewUpdatedItemUiState = {
  viewName: string;
  viewData: Item;
};

const KEY_CHILD_ITEMS_TO_SAVE = '$childItemsToSave';
const KEY_CHILD_ITEMS_TO_DELETE = '$childItemsToDelete';

@Directive({
  selector: '[appInMemoryListViewProvider]',
  providers: [
    {
      provide: ListViewProviderDirective,
      useExisting: InMemoryListViewProviderDirective
    }
  ]
})
export class InMemoryListViewProviderDirective
  extends ListViewProviderDirective<ListView>
  implements OnInit
{
  private parentFormService = inject(DynamicFormService);
  private parentItemViewProvider = inject(ItemViewProviderDirective);

  override addItem(item: object): void {
    const uid = uuidv4();
    const uidAttrKey = `${(item as any)['$itemType']}~uuid`;
    (item as any)[uidAttrKey] = `${Date.now()}${uid}`;
    super.addItem(item);
  }

  override updateItem(item: object, index: number): void {
    super.updateItem(item, index);

    if (!item) {
      return;
    }

    const control: FormControl<Array<CompoundViewUpdatedItemUiState>> =
      this.getFormControl(KEY_CHILD_ITEMS_TO_SAVE) as FormControl<
        Array<CompoundViewUpdatedItemUiState>
      >;
    this.propagateToParentForm(control, item);
  }

  private propagateToParentForm(
    control: FormControl<Array<CompoundViewUpdatedItemUiState>>,
    item: object,
    removeOnly?: boolean
  ) {
    const items = control.value || [];
    const updatedItem = new ItemInfo(item);
    const existingItemIndex = this.findItem(items, item);
    const itemChange = { viewName: this.viewName, viewData: item };
    if (existingItemIndex >= 0) {
      if (removeOnly) {
        items.splice(existingItemIndex, 1);
      } else {
        items.splice(existingItemIndex, 1, itemChange);
      }
    } else {
      console.log(
        `Item with id ${updatedItem.id} not found in items waiting to be saved, will be added as new item`
      );
      items.push(itemChange);
    }
    control.markAsDirty();
  }

  private findItem(items: CompoundViewUpdatedItemUiState[], updatedItem: Item) {
    return items.findIndex((i) => {
      const currentItemInfo = new ItemInfo(i.viewData);
      const currentItem = i.viewData as any;
      const uidKey = `${currentItemInfo.itemType}~uuid`;
      const updatedItemInfo = new ItemInfo(updatedItem);
      const updated = updatedItem as any;
      return (
        currentItemInfo.itemType === updatedItemInfo.itemType &&
        // special case - for unsaved items use locally defined uid key
        ((String(updatedItemInfo.id) === '0' &&
          currentItem[uidKey] === updated[uidKey]) ||
          (String(updatedItemInfo.id) !== '0' &&
            currentItemInfo.id === updatedItemInfo.id))
      );
    });
  }

  private getFormControl(controlName: string) {
    let control: AbstractControl | null =
      this.parentFormService.getFormControl(controlName);
    control = this.initFormControl(controlName, control);
    return control;
  }

  private initFormControl(
    controlName: string,
    control: AbstractControl<any, any> | null
  ) {
    if (!control) {
      control = new FormControl([]);
      this.parentFormService.formGroup.addControl(controlName, control);
    }
    return control;
  }

  override deleteItemAtIndex(
    item: BusinessItem,
    index: number
  ): Observable<unknown> {
    const updateControl = this.getFormControl(KEY_CHILD_ITEMS_TO_SAVE);
    const deleteControl = this.getFormControl(
      KEY_CHILD_ITEMS_TO_DELETE
    ) as FormControl;
    const items: CompoundViewUpdatedItemUiState[] = updateControl.value;
    const deletedItem = new ItemInfo(item.data || item);
    const existingItemIndex = this.findItem(items, deletedItem);
    if (existingItemIndex >= 0) {
      items.splice(existingItemIndex, 1);
    } else {
      // already removed from save list, add to delete list, as it's obviously an item already saved to backend
      this.propagateToParentForm(deleteControl, item.data);
    }
    return super.deleteItemAtIndex(item, index);
  }

  ngOnInit(): void {
    this.addSubscription(
      this.parentItemViewProvider.item$
        .pipe(
          skip(1),
          tap(() => {
            const saveControl: FormControl<
              Array<CompoundViewUpdatedItemUiState>
            > = this.getFormControl(KEY_CHILD_ITEMS_TO_SAVE) as FormControl<
              Array<CompoundViewUpdatedItemUiState>
            >;
            saveControl.setValue([]);
            saveControl.markAsDirty();
            const deleteControl: FormControl<
              Array<CompoundViewUpdatedItemUiState>
            > = this.getFormControl(KEY_CHILD_ITEMS_TO_DELETE) as FormControl<
              Array<CompoundViewUpdatedItemUiState>
            >;
            deleteControl.setValue([]);
            deleteControl.markAsDirty();
          })
        )
        .subscribe()
    );
  }

  cancelUpdateItem(item: any) {
    const control: FormControl<Array<CompoundViewUpdatedItemUiState>> =
      this.getFormControl(KEY_CHILD_ITEMS_TO_SAVE) as FormControl<
        Array<CompoundViewUpdatedItemUiState>
      >;
    this.propagateToParentForm(control, item, true);
  }
}
